import React, { useState, useEffect } from 'react';
import { connect } from "react-redux";
import { useDispatch } from '../reducers/store';
import { setCrumb } from '../reducers/global';
import { createExpense, deleteExpense } from '../reducers/expenses';
import SwitchEditComponent from '../components/SwitchEdit'
import ItemEditComponent from '../components/TextEdit'
import ItemDateComponent from '../components/DateEdit'
import moment from 'moment'
import DownloadLink from "react-download-link";
import urls from '../network/urls';
import Select from './SelectObjComponent'
import Alert from "./glass/GlassAlert";
import { colors } from '../theme';


const getDefaultExpense = () => ({
    expenseDate: new Date()
});

const AddExpenseComponent = ({ expense, error, updateshowAdd, dispatchCreateExpense, dispatchDeleteExpense, accounts }) => {

    const [addExpense, setAddExpense] = useState(expense || getDefaultExpense())
    const [edit, setEdit] = useState(!(expense && expense._id))
    const [photo, setPhoto] = useState(null)
    const [photoPreview, setPhotoPreview] = useState(null)
    const [loadedExpenseId, setLoadedExpenseId] = useState(expense?._id)
    const [saving, setSaving] = useState(false)
    const [showPreview, setShowPreview] = useState(false)
    const [dirty, setDirty] = useState(false)

    // Topbar sub-breadcrumb: Expenses › <title>
    const _crumbDispatch = useDispatch();
    const crumbTitle = addExpense ? (addExpense._id ? 'Expense Details' : 'New Expense') : null;
    useEffect(() => {
        _crumbDispatch(setCrumb(crumbTitle));
        return () => _crumbDispatch(setCrumb(null));
    }, [crumbTitle]);

    // Light-glass header button styles
    const hdrIconBtn = { background: 'rgba(15,31,46,0.06)', border: 'none', color: 'var(--text-secondary)', width: 36, height: 36, borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' };
    const hdrTealBtn = { background: 'linear-gradient(135deg, var(--primary), var(--primary-dark))', border: 'none', color: '#fff', padding: '8px 16px', borderRadius: 9, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontWeight: 700, fontFamily: 'var(--font-display)', fontSize: 13 };
    const hdrGhostBtn = { background: 'rgba(15,31,46,0.06)', border: '1px solid rgba(15,31,46,0.1)', color: 'var(--text-secondary)', padding: '8px 16px', borderRadius: 9, cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font-display)', fontSize: 13 };

    // Reset form only when loading a different expense (by _id)
    useEffect(() => {
        const newId = expense?._id;
        if (newId !== loadedExpenseId) {
            setAddExpense(expense || getDefaultExpense());
            setEdit(!(expense && expense._id));
            setPhoto(null);
            setPhotoPreview(null);
            setLoadedExpenseId(newId);
            setDirty(false);
        }
    }, [expense, loadedExpenseId]);

    // Exit (back) — confirm if there are unsaved changes
    const requestBack = () => {
        if (!dirty && !photo) { updateshowAdd(false); return; }
        Alert.fire({
            title: 'Discard unsaved changes?',
            text: 'Your changes to this expense will be lost.',
            showDenyButton: true,
            showCancelButton: false,
            confirmButtonText: 'Keep editing',
            denyButtonText: 'Discard',
        }).then((r) => { if (r.isDenied) updateshowAdd(false); });
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPhoto(file);
            setPhotoPreview(URL.createObjectURL(file));
        }
    };

    const cancelChange = () => {
        setAddExpense(expense || {})
    }

    const saveExpense = () => {
        setSaving(true);
        dispatchCreateExpense(photo, addExpense, done);
        function done(error) {
            setSaving(false);
            if (!error) setEdit(false);
        }
    }

    const deleteExpense = () => {
        dispatchDeleteExpense(addExpense._id, done);
        function done(error) {
            if (!error) updateshowAdd(false);
        }
    }

    const onChangeText = (key, value) => {
        let newExpense = {
            ...addExpense,
            [key]: value
        };

        setAddExpense(newExpense)
        setDirty(true)
    }

    // Unified design styles
    const darkHeaderStyle = {
        background: '#1a3a4a',
        borderRadius: '8px 8px 0 0',
        padding: '16px 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
    };

    const darkHeaderTitleStyle = {
        margin: 0,
        fontSize: 18,
        fontWeight: 600,
        color: 'white'
    };

    const cardStyle = {
        background: 'transparent',
        padding: '20px 22px 40px 22px'
    };

    const headerStyle = {
        fontSize: 15,
        fontWeight: 600,
        color: '#1a3a4a',
        marginBottom: 16,
        paddingBottom: 10,
        borderBottom: '2px solid #19a6b7',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    };

    const sectionStyle = {
        marginBottom: 16
    };

    const statusBadgeStyle = {
        display: 'inline-block',
        padding: '6px 16px',
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 500
    };

    const buttonStyle = {
        padding: '10px 24px',
        borderRadius: 6,
        border: 'none',
        fontWeight: 500,
        cursor: 'pointer',
        fontSize: 14,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8
    };

    // Get the current receipt image URL
    const receiptImageUrl = photoPreview || addExpense.photo;
    const hasReceipt = !!receiptImageUrl;

    return (
        <div style={{ width: '100%' }}>
            {/* Receipt Preview Modal */}
            {showPreview && hasReceipt && (
                <div id="receiptPreviewModal"
                    tabIndex="-1"
                    className="modal fade show"
                    role="dialog"
                    data-backdrop="true"
                    aria-labelledby="receiptPreviewModalTitle"
                    aria-modal="true"
                    style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.7)' }}
                    onClick={() => setShowPreview(false)} >

                    <div className="modal-dialog modal-dialog-centered modal-xl" role="document"
                        style={{ maxWidth: '90%', margin: '5vh auto' }}
                        onClick={(e) => e.stopPropagation()}>
                        <div className="modal-content" style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
                            <div style={{
                                background: '#1a3a4a',
                                padding: '16px 24px',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                borderRadius: '8px 8px 0 0'
                            }}>
                                <h4 style={{ margin: 0, color: 'white', fontWeight: 600 }}>
                                    <i className="fas fa-receipt" style={{ marginRight: 10 }}></i>
                                    Receipt Preview
                                </h4>
                                <button type="button" onClick={() => setShowPreview(false)} style={{
                                    background: 'transparent',
                                    border: 'none',
                                    color: 'white',
                                    fontSize: 20,
                                    cursor: 'pointer'
                                }}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                            <div style={{
                                background: '#fff',
                                padding: 24,
                                borderRadius: '0 0 8px 8px',
                                textAlign: 'center',
                                maxHeight: '75vh',
                                overflow: 'auto'
                            }}>
                                <img
                                    src={receiptImageUrl}
                                    alt="Receipt"
                                    style={{
                                        maxWidth: '100%',
                                        maxHeight: '70vh',
                                        borderRadius: 8,
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)'
                                    }}
                                />
                            </div>
                            <div style={{
                                padding: '12px 24px',
                                background: '#f8f9fa',
                                borderRadius: '0 0 8px 8px',
                                display: 'flex',
                                justifyContent: 'flex-end',
                                gap: 12
                            }}>
                                <button
                                    type="button"
                                    onClick={() => setShowPreview(false)}
                                    style={{
                                        ...buttonStyle,
                                        background: '#fff',
                                        color: '#666',
                                        border: '1px solid #ddd'
                                    }}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Error Alert */}
            {!!error && (
                <div style={{
                    background: '#fef0f0',
                    border: '1px solid #e84651',
                    padding: '12px 16px',
                    color: '#e84651',
                    fontWeight: 500
                }}>
                    <i className="fas fa-exclamation-circle" style={{ marginRight: 8 }}></i>
                    {error}
                </div>
            )}

            {addExpense && (
                <div style={{
                    margin: '2px 0 24px',
                    borderRadius: 18,
                    overflow: 'hidden',
                    border: '1px solid var(--glass-border, rgba(255,255,255,0.65))',
                    boxShadow: '0 18px 50px rgba(15,31,46,0.16)',
                    background: 'rgba(255,255,255,0.55)',
                    WebkitBackdropFilter: 'blur(22px) saturate(1.4)',
                    backdropFilter: 'blur(22px) saturate(1.4)'
                }}>
                    {/* Frosted-glass header — transparent over the single card surface */}
                    <div style={{
                        background: 'transparent',
                        borderBottom: '1px solid rgba(15,31,46,0.07)',
                        padding: '12px 20px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }} className="no-print">
                        {/* Left: Back button and Title */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                            <button
                                type="button"
                                onClick={requestBack}
                                style={hdrIconBtn}
                                title="Back to list"
                            >
                                <i className="fas fa-arrow-left"></i>
                            </button>
                            <div>
                                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, fontFamily: 'var(--font-display)', color: 'var(--text)' }}>
                                    {addExpense._id ? 'Expense Details' : 'New Expense'}
                                </h2>
                            </div>
                            <span style={{
                                ...statusBadgeStyle,
                                background: addExpense.paid ? 'rgba(27,159,105,0.14)' : 'rgba(218,74,79,0.14)',
                                color: addExpense.paid ? 'var(--success)' : 'var(--danger)',
                                border: 'none',
                                fontSize: 11,
                                fontWeight: 700,
                                padding: '4px 12px'
                            }}>
                                {addExpense.paid ? 'Paid' : 'Unpaid'}
                            </span>
                        </div>

                        {/* Right: Action Icons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {/* View Mode Actions */}
                            {!edit && addExpense._id && (
                                <>
                                    {hasReceipt && (
                                        <button
                                            type="button"
                                            onClick={() => setShowPreview(true)}
                                            style={hdrIconBtn}
                                            title="Preview Receipt"
                                        >
                                            <i className="fas fa-eye"></i>
                                        </button>
                                    )}
                                    <div style={{ width: 1, height: 24, background: 'rgba(15,31,46,0.12)', margin: '0 4px' }}></div>
                                    <button
                                        type="button"
                                        onClick={() => setEdit(true)}
                                        style={hdrTealBtn}
                                        title="Edit"
                                    >
                                        <i className="fas fa-edit"></i> Edit
                                    </button>
                                </>
                            )}

                            {/* Edit Mode Actions */}
                            {edit && (
                                <>
                                    {addExpense._id && (
                                        <button
                                            type="button"
                                            onClick={() => {
                                                Alert.fire({
                                                    title: 'Are you sure you want to delete this expense?',
                                                    showDenyButton: true,
                                                    showCancelButton: false,
                                                    confirmButtonText: 'Yes',
                                                    denyButtonText: 'No',
                                                }).then((result) => {
                                                    if (result.isConfirmed) {
                                                        deleteExpense();
                                                    }
                                                });
                                            }}
                                            style={{ ...hdrIconBtn, background: 'rgba(218,74,79,0.12)', color: 'var(--danger)' }}
                                            title="Delete"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    )}
                                    {addExpense._id && <div style={{ width: 1, height: 24, background: 'rgba(15,31,46,0.12)', margin: '0 4px' }}></div>}
                                    <button
                                        type="button"
                                        onClick={() => saveExpense()}
                                        disabled={saving}
                                        style={{ ...hdrTealBtn, opacity: saving ? 0.7 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}
                                    >
                                        {saving ? <><i className="fas fa-spinner fa-spin"></i> Saving...</> : <><i className="fas fa-save"></i> Save</>}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* White Content Area */}
                    <div style={cardStyle}>
                        {edit && (
                            <div style={sectionStyle}>
                                <SwitchEditComponent invert={true} header={'Mark as Paid'} onChangeText={() => onChangeText('paid', !addExpense.paid)} prop={'paid'} value={addExpense.paid} />
                            </div>
                        )}

                        {/* Expense Information */}
                        <h4 style={headerStyle}>Expense Information</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16 }}>
                        <div style={sectionStyle}>
                            <ItemEditComponent viewOnly={!edit} invert={true} header={'Vendor / To'} onChangeText={onChangeText} prop={'to'} value={addExpense.to} />
                        </div>
                        <div style={sectionStyle}>
                            <ItemDateComponent viewOnly={!edit} invert={true} header={'Expense Date'} onChangeText={onChangeText} prop={'expenseDate'} value={addExpense.expenseDate} />
                        </div>
                        <div style={sectionStyle}>
                            <ItemEditComponent viewOnly={!edit} invert={true} keyboardType={'numeric'} header={'Amount (R)'} onChangeText={onChangeText} prop={'price'} value={addExpense.price} />
                        </div>
                        <div style={sectionStyle}>
                            <Select viewOnly={!edit} key={'Ledger'} header={'Expense Category'} options={accounts.map(i => ({
                                label: i.account + ': ' + i.name,
                                value: i._id
                            }))} onChangeText={onChangeText} prop={'ledgerAccountId'} value={addExpense.ledgerAccountId} />
                        </div>
                    </div>
                    <div style={{ ...sectionStyle, marginTop: 8 }}>
                        <ItemEditComponent viewOnly={!edit} invert={true} header={'Description'} onChangeText={onChangeText} prop={'description'} value={addExpense['description']} />
                    </div>

                    {/* Warranty Information */}
                    <h4 style={{ ...headerStyle, marginTop: 24 }}>Warranty Information</h4>
                    <div style={sectionStyle}>
                        <SwitchEditComponent viewOnly={!edit} invert={true} header={'Has Warranty'} onChangeText={() => onChangeText('hasWarranty', !addExpense.hasWarranty)} prop={'hasWarranty'} value={!!addExpense.hasWarranty} />
                    </div>
                    {!!addExpense.hasWarranty && (
                        <div style={sectionStyle}>
                            <ItemDateComponent viewOnly={!edit} header={'Warranty Valid Until'} onChangeText={onChangeText} prop={'warrantyValidDate'} value={addExpense.warrantyValidDate} />
                        </div>
                    )}

                    {/* Receipt / Attachment */}
                    <h4 style={{ ...headerStyle, marginTop: 24 }}>Receipt / Attachment</h4>
                    {edit && (
                        <div style={{
                            border: '2px dashed #ddd',
                            borderRadius: 8,
                            padding: 24,
                            textAlign: 'center',
                            background: '#fafafa',
                            marginBottom: 16
                        }}>
                            <i className="fas fa-cloud-upload-alt" style={{ fontSize: 32, color: '#19a6b7', marginBottom: 12, display: 'block' }}></i>
                            <label style={{
                                display: 'inline-block',
                                padding: '10px 20px',
                                background: '#19a6b7',
                                color: 'white',
                                borderRadius: 6,
                                cursor: 'pointer',
                                fontWeight: 500
                            }}>
                                Choose File
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handlePhotoChange}
                                    style={{ display: 'none' }}
                                />
                            </label>
                            <p style={{ margin: '12px 0 0', color: '#888', fontSize: 13 }}>
                                Upload receipt image (JPG, PNG)
                            </p>
                        </div>
                    )}

                    {photoPreview && (
                        <div style={{ textAlign: 'center' }}>
                            <img src={photoPreview} alt="Receipt preview" style={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 8,
                                border: '1px solid #eee',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }} />
                        </div>
                    )}

                    {!photoPreview && addExpense.photo && (
                        <div style={{ textAlign: 'center' }}>
                            <img src={addExpense.photo} alt="Receipt" style={{
                                maxWidth: '100%',
                                maxHeight: 300,
                                borderRadius: 8,
                                border: '1px solid #eee',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }} />
                        </div>
                    )}

                    {!photoPreview && !addExpense.photo && !edit && (
                        <p style={{ color: '#888', textAlign: 'center', margin: 0 }}>No receipt attached</p>
                    )}
                    </div>
                </div>
            )}

            {/* Recurring Options - Only for new expenses */}
            {addExpense && !addExpense._id && (
                <div style={{ background: '#f8f9fa', borderTop: '1px solid #eee' }}>
                    <div style={{ padding: '20px 20px 40px 20px' }}>
                        <h4 style={headerStyle}>Recurring Options</h4>
                        <div style={sectionStyle}>
                            <SwitchEditComponent viewOnly={!edit} invert={true} header={'Make Recurring'} onChangeText={() => onChangeText('isRecurring', !addExpense.isRecurring)} prop={'isRecurring'} value={!!addExpense.isRecurring} />
                        </div>
                        {!!addExpense.isRecurring && (
                            <div style={sectionStyle}>
                                <Select viewOnly={!edit} key={'Repeat'} header={'Frequency'} options={[
                                    { label: 'Daily', value: 'day' },
                                    { label: 'Weekly', value: 'week' },
                                    { label: 'Monthly', value: 'month' },
                                    { label: 'Yearly', value: 'year' }
                                ]} onChangeText={onChangeText} prop={'recurringType'} value={addExpense.recurringType} />
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
};

class AddExpenseScreen extends React.Component {

    render() {
        return (
            <AddExpenseComponent {...this.props} />
        );
    }
}

const mapStateToProps = state => ({
    authUser: state.auth.user,
    expense: state.expenses.expense,
    error: state.expenses.error,
    accounts: state.accounting.accounts
})

const mapDispatchToProps = {
    dispatchDeleteExpense: (id, callback) => deleteExpense(id, callback),
    dispatchCreateExpense: (photo, invoice, callback) => createExpense(photo, invoice, callback),
}

export default connect(mapStateToProps, mapDispatchToProps)(AddExpenseScreen)
